import {Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {SortTableCellKebabMenu} from '../../../../../shared/models/sort-table/cell-classes';
import {OptionGroup} from '../../../../../shared/models/option';
import {CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {BaseCellComponent} from '../_base-cell/base-cell.component';
import {SortTableInstanceService} from '../../../../../core/services/sort-table-instance.service';
import {Subscription} from 'rxjs';
import {SortTableRow} from '../../../../../shared/models/sort-table/types';
import {PopoverMenuComponent} from '../../../popover-menu/popover-menu.component';
import {TableMenuOptionId} from '../../../../../shared/constants/menu-option-ids';

@Component({
  selector: 'motn-sort-table-cell-kebab-menu',
  template: `
    @if (!bulkSelectActive) {
      @if (menuOpen) {
        <motn-popover-menu
          [menuTitle]="cell.menuTitle"
          [optionGroup]="optionGroup"
          [overlayPositions]="overlayPositions"
          [overlayPush]="true"
          [overlayLockPosition]="false"
          [viewportMargin]="menuViewportMargin"
          (clickedOption$)="closeMenu()"
          (clickedOutsideMenu$)="closeMenu()"
        ></motn-popover-menu>
      }

      <div
        class="kebab"
        (click)="openMenu()"
        cdkOverlayOrigin
        #cellKebab="cdkOverlayOrigin"
      >
        <i class="fa-solid fa-ellipsis-vertical"></i>
      </div>
    }

    @if (bulkSelectActive) {
      @if (!bulkSelected) {
        <div
          (click)="selectRow()"
          class="check"
        >
          <i class="fa-regular fa-circle-xmark"></i>
        </div>
      }
      @if (bulkSelected) {
        <div
          (click)="bulkDeselect()"
          class="check on"
        >
          <i class="fa-solid fa-circle-check"></i>
        </div>
      }
    }
  `,
  styleUrls: ['./sort-table-cell-kebab-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PopoverMenuComponent,
    CdkOverlayOrigin,
  ],
})
export class SortTableCellKebabMenuComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() cell: SortTableCellKebabMenu;
  @Input() row: SortTableRow;

  menuOpen: boolean;
  menuViewportMargin: number;

  get optionGroup(): OptionGroup {
    if (this.cell.isLoading) {
      return new OptionGroup([
        {
          id: 'loading',
          name: '',
          icon: 'fa-regular fa-spin fa-loader',
        }
      ]);
    }

    const options = [...this.cell.options];

    if (this.cell.bulkSelectEnabled) {
      options.unshift({
        id: TableMenuOptionId.BULK_SELECT,
        name: 'Bulk Select',
        callback: () => {
          this.selectRow();
          this.tableService.activateBulkSelect();
        },
      });
    }

    return new OptionGroup(options);
  }

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    }
  ];

  private _bulkSelected: boolean = false;

  get bulkSelected(): boolean {
    return this._bulkSelected;
  }

  bulkSelectActive: boolean = false;
  private bulkSelectActiveSub: Subscription;
  private bulkSelectionIndexSub: Subscription;

  constructor(
    protected tableService: SortTableInstanceService,
  ) {
    super(tableService);
  }

  protected onInit(): void {
    // Small viewport margin so CDK knows to avoid viewport edges
    this.menuViewportMargin = 8;

    if (this.cell.bulkSelectEnabled) {
      this.bulkSelectActiveSub = this.tableService.bulkSelectActive$.subscribe({
        next: (enabled) => {
          this.bulkSelectActive = enabled;
          this.menuOpen = false;
          this._bulkSelected = this.tableService.isRowSelected(this.row);
        },
      });

      this._bulkSelected = this.tableService.isRowSelected(this.row);
    }
  }

  ngOnDestroy() {
    this.bulkSelectActiveSub?.unsubscribe();
    this.bulkSelectionIndexSub?.unsubscribe();
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  openMenu(): void {
    this.menuOpen = true;

    if (this.cell?.clickCallback) {
      this.cell.clickCallback();
    }
  }

  selectRow() {
    this._bulkSelected = true;

    const success = this.tableService.selectRow(this.row);

    if (!success) {
      this._bulkSelected = false;
    }
  }

  bulkDeselect() {
    this._bulkSelected = false;
    this.tableService.deselectRow(this.row);
  }
}
